import { useEffect, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { subscribeToPayments } from '../../services/paymentService';
import { subscribeToSubscriptions } from '../../services/subscriptionService';
import { subscribeToUsers } from '../../services/userService';
import { COLORS } from '../../utils/constants';
import AdvancedAnalytics from './AdvancedAnalytics';
import AnalyticsNavTabs from '../../components/analytics/AnalyticsNavTabs';

const Analytics = () => {
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalRevenue: 0,
    activeStudents: 0,
    activeSubscriptions: 0,
    conversionRate: 0,
  });

  const [revenueData, setRevenueData] = useState([]);
  const [userDistribution, setUserDistribution] = useState([]);
  const [classDistribution, setClassDistribution] = useState([]);

  // Placeholders
  const subjectData = [
    { subject: 'Mathematics', students: 0, color: COLORS.primary },
    { subject: 'English', students: 0, color: COLORS.success },
    { subject: 'Physics', students: 0, color: COLORS.emerald },
    { subject: 'Chemistry', students: 0, color: COLORS.warning },
    { subject: 'Biology', students: 0, color: COLORS.danger },
  ];

  useEffect(() => {
    let users = [];
    let payments = [];
    let subscriptions = [];
    let loadedCount = 0;
    
    // Set a timeout to ensure loading doesn't hang forever
    const loadingTimeout = setTimeout(() => {
      console.warn('⚠️ Loading timeout - forcing render with partial data. Loaded:', {
        users: users.length,
        payments: payments.length, 
        subscriptions: subscriptions.length,
        loadedCount
      });
      setLoading(false);
    }, 5000);

    const processData = () => {
      // Only process when all 3 listeners have returned initial data
      if (loadedCount < 3) {
        console.log('Waiting for data sources. Current loadedCount:', loadedCount);
        return;
      }
      
      clearTimeout(loadingTimeout);
      console.log('✓ All data loaded successfully');

      // 1. Calculate Active Students
      const students = users.filter(u => u.role === 'student');
      const teachers = users.filter(u => u.role === 'teacher');
      const admins = users.filter(u => u.role === 'admin');
      
      const activeStudents = students.length;

      // 2. User Distribution
      setUserDistribution([
        { name: 'Students', value: students.length, color: COLORS.primary },
        { name: 'Teachers', value: teachers.length, color: COLORS.success },
        { name: 'Admins', value: admins.length, color: COLORS.warning },
      ]);

      // 3. Class Distribution
      const ss1Count = students.filter(s => s.classLevel?.toLowerCase() === 'ss1').length;
      const ss2Count = students.filter(s => s.classLevel?.toLowerCase() === 'ss2').length;
      const ss3Count = students.filter(s => s.classLevel?.toLowerCase() === 'ss3').length;
      const utmeCount = students.filter(s => s.classLevel?.toLowerCase() === 'utme').length;
      
      setClassDistribution([
        { class: 'SS1', count: ss1Count },
        { class: 'SS2', count: ss2Count },
        { class: 'SS3', count: ss3Count },
        { class: 'UTME', count: utmeCount },
      ]);

      // 4. Subscriptions
      const activeSubs = subscriptions.filter(s => s.status === 'active').length;

      // 5. Payments & Revenue
      let totalRev = 0;
      const monthlyData = {}; // { 'Jan 2026': { month: 'Jan 2026', revenue: 0, students: 0 } }
      
      payments.forEach(p => {
        if (p.status === 'success') {
          const amount = parseFloat(String(p.amount).replace(/[^0-9.-]+/g, '') || 0);
          totalRev += amount;
          
          let date;
          if (p.createdAt?.toDate) {
            date = p.createdAt.toDate();
          } else if (p.createdAt) {
            date = new Date(p.createdAt);
          } else {
            date = new Date();
          }
          
          const monthYear = date.toLocaleString('default', { month: 'short', year: 'numeric' });
          if (!monthlyData[monthYear]) {
            monthlyData[monthYear] = { month: monthYear, revenue: 0, students: 0 };
          }
          monthlyData[monthYear].revenue += amount;
        }
      });

      // Map registrations to monthly data
      students.forEach(s => {
          let date;
          if (s.createdAt?.toDate) {
            date = s.createdAt.toDate();
          } else if (s.createdAt) {
            date = new Date(s.createdAt);
          } else {
            date = new Date();
          }
          const monthYear = date.toLocaleString('default', { month: 'short', year: 'numeric' });
          if (!monthlyData[monthYear]) {
            monthlyData[monthYear] = { month: monthYear, revenue: 0, students: 0 };
          }
          monthlyData[monthYear].students += 1;
      });

      // Sort chronological
      const sortedMonths = Object.values(monthlyData).sort((a, b) => {
        return new Date(a.month) - new Date(b.month);
      });
      setRevenueData(sortedMonths.length > 0 ? sortedMonths : [{ month: 'No Data', revenue: 0, students: 0 }]);

      // 6. Conversion Rate
      const conversionRate = activeStudents > 0 ? Math.round((activeSubs / activeStudents) * 100) : 0;

      setMetrics({
        totalRevenue: totalRev,
        activeStudents,
        activeSubscriptions: activeSubs,
        conversionRate,
      });

      setLoading(false);
    };

    let usersLoaded = false;
    let unsubUsers = () => {};
    let unsubPayments = () => {};
    let unsubSubs = () => {};
    
    try {
      unsubUsers = subscribeToUsers((data) => {
        users = data || [];
        if (!usersLoaded) { 
          usersLoaded = true; 
          loadedCount++;
          console.log('✓ Users loaded:', users.length, 'items'); 
        }
        processData();
      });
    } catch (error) {
      console.error('❌ Error subscribing to users:', error);
      if (!usersLoaded) { 
        usersLoaded = true; 
        loadedCount++; 
        processData();
      }
    }

    let paymentsLoaded = false;
    try {
      unsubPayments = subscribeToPayments((data) => {
        payments = data || [];
        if (!paymentsLoaded) { 
          paymentsLoaded = true; 
          loadedCount++;
          console.log('✓ Payments loaded:', payments.length, 'items');
        }
        processData();
      });
    } catch (error) {
      console.error('❌ Error subscribing to payments:', error);
      if (!paymentsLoaded) { 
        paymentsLoaded = true; 
        loadedCount++; 
        processData();
      }
    }

    let subsLoaded = false;
    try {
      unsubSubs = subscribeToSubscriptions((data) => {
        subscriptions = data || [];
        if (!subsLoaded) { 
          subsLoaded = true; 
          loadedCount++;
          console.log('✓ Subscriptions loaded:', subscriptions.length, 'items');
        }
        processData();
      });
    } catch (error) {
      console.error('❌ Error subscribing to subscriptions:', error);
      if (!subsLoaded) { 
        subsLoaded = true; 
        loadedCount++; 
        processData();
      }
    }

    return () => {
      clearTimeout(loadingTimeout);
      unsubUsers();
      unsubPayments();
      unsubSubs();
    };
  }, []);

  if (loading) {
    return <div className="text-center py-12 text-gray-500"> Loading...</div>;
  }

  // Formatting currency
  const formatCurrency = (amount) => {
    if (amount >= 1000000) return `₦${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `₦${(amount / 1000).toFixed(1)}K`;
    return `₦${amount.toLocaleString()}`;
  };

  return (
    <div className="w-full">
      {/* Page Header */}
      <div className="mb-4 md:mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-darkNavy mb-1 md:mb-2 font-poppins">Analytics & Reporting</h1>
        <p className="text-sm md:text-base text-gray-500">View platform performance, revenue, and usage metrics</p>
      </div>

      {/* Navigation Tabs */}
      <AnalyticsNavTabs />

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-6 md:mb-8">
        <div className="bg-white p-3 md:p-5 rounded-xl border border-gray-200 shadow-sm">
          <p className="text-xs md:text-sm text-gray-500 mb-1">Total Revenue</p>
          <p className="text-lg md:text-2xl font-bold text-darkNavy">{formatCurrency(metrics.totalRevenue)}</p>
        </div>
        <div className="bg-white p-3 md:p-5 rounded-xl border border-gray-200 shadow-sm">
          <p className="text-xs md:text-sm text-gray-500 mb-1">Active Students</p>
          <p className="text-lg md:text-2xl font-bold text-darkNavy">{metrics.activeStudents}</p>
        </div>
        <div className="bg-white p-3 md:p-5 rounded-xl border border-gray-200 shadow-sm">
          <p className="text-xs md:text-sm text-gray-500 mb-1">Active Subscriptions</p>
          <p className="text-lg md:text-2xl font-bold text-darkNavy">{metrics.activeSubscriptions}</p>
        </div>
        <div className="bg-white p-3 md:p-5 rounded-xl border border-gray-200 shadow-sm">
          <p className="text-xs md:text-sm text-gray-500 mb-1">Avg. Quiz Score</p>
          <p className="text-lg md:text-2xl font-bold text-gray-400">N/A</p>
          <p className="text-[10px] text-gray-400 mt-1">Pending App Release</p>
        </div>
        <div className="bg-white p-3 md:p-5 rounded-xl border border-gray-200 shadow-sm col-span-2 lg:col-span-1">
          <p className="text-xs md:text-sm text-gray-500 mb-1">Conversion Rate</p>
          <p className="text-lg md:text-2xl font-bold text-darkNavy">{metrics.conversionRate}%</p>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
        {/* Revenue Trend */}
        <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 gap-2 sm:gap-0">
            <h2 className="text-base md:text-lg font-bold text-darkNavy">Revenue Trend</h2>
          </div>
          <div className="overflow-x-auto">
            <ResponsiveContainer width="100%" height={250} minWidth={280}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <YAxis stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="revenue"
                  stroke={COLORS.primary}
                  strokeWidth={3}
                  dot={{ fill: COLORS.primary, r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Student Growth */}
        <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
          <h2 className="text-base md:text-lg font-bold text-darkNavy mb-4">Student Growth</h2>
          <div className="overflow-x-auto">
            <ResponsiveContainer width="100%" height={250} minWidth={280}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="month" stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <YAxis stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="students"
                  stroke={COLORS.success}
                  strokeWidth={3}
                  dot={{ fill: COLORS.success, r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-4 md:mb-6">
        {/* Popular Subjects */}
        <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
          <h2 className="text-base md:text-lg font-bold text-darkNavy mb-4">Most Popular Subjects (Coming Soon)</h2>
          <div className="overflow-x-auto opacity-50">
            <ResponsiveContainer width="100%" height={250} minWidth={280}>
              <BarChart data={subjectData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="subject" stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <YAxis stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <Tooltip />
                <Bar dataKey="students" fill={COLORS.primary} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* User Distribution */}
        <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm">
          <h2 className="text-base md:text-lg font-bold text-darkNavy mb-4">User Distribution</h2>
          <div className="overflow-x-auto">
            <ResponsiveContainer width="100%" height={250} minWidth={280}>
              <PieChart>
                <Pie
                  data={userDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {userDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-col sm:flex-row sm:justify-center gap-3 sm:gap-6 mt-4">
            {userDistribution.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: item.color }}></div>
                <span className="text-xs md:text-sm text-gray-600">{item.name}</span>
                <span className="text-xs md:text-sm font-semibold text-gray-900">{item.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Class Distribution */}
      <div className="bg-white rounded-xl md:rounded-2xl border border-gray-200 p-4 md:p-6 shadow-sm mb-4 md:mb-6">
        <h2 className="text-base md:text-lg font-bold text-darkNavy mb-4">Student Distribution by Class</h2>
        <div className="overflow-x-auto">
          <ResponsiveContainer width="100%" height={220} minWidth={280}>
            <BarChart data={classDistribution} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="category" dataKey="class" stroke="#94a3b8" style={{ fontSize: '12px' }} />
              <YAxis type="number" stroke="#94a3b8" style={{ fontSize: '12px' }} />
              <Tooltip />
              <Bar dataKey="count" fill={COLORS.emerald} radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Advanced Analytics Section */}
      <div className="mt-8">
        <div className="mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-darkNavy mb-2">Advanced Analytics</h2>
          <p className="text-sm md:text-base text-gray-500">In-depth insights with custom date ranges and detailed breakdowns</p>
        </div>
        <AdvancedAnalytics />
      </div>
    </div>
  );
};

export default Analytics;
